import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { zh } from './zh'
import { en } from './en'

export type Locale = 'zh' | 'en'

const dicts: Record<Locale, Record<string, string>> = {
  zh: flatten(zh),
  en: flatten(en),
}

function flatten(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k
    if (typeof v === 'string') out[key] = v
    else if (v && typeof v === 'object') Object.assign(out, flatten(v as Record<string, unknown>, key))
  }
  return out
}

export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string

interface I18nCtx {
  locale: Locale
  setLocale: (l: Locale) => void
  t: TranslateFn
}

const Ctx = createContext<I18nCtx>({
  locale: 'zh',
  setLocale: () => {},
  t: (k) => k,
})

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() => {
    const saved = localStorage.getItem('dayi.locale')
    if (saved === 'zh' || saved === 'en') return saved
    return navigator.language?.toLowerCase().startsWith('zh') ? 'zh' : 'en'
  })

  const t: TranslateFn = (key, vars) => {
    let s = dicts[locale][key] ?? dicts.zh[key] ?? key
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        s = s.replaceAll(`{${k}}`, String(v))
      }
    }
    return s
  }

  const value = useMemo<I18nCtx>(() => {
    const setLocaleWrapped = (l: Locale) => {
      setLocale(l)
      localStorage.setItem('dayi.locale', l)
      document.documentElement.lang = l === 'zh' ? 'zh-CN' : 'en'
    }
    return { locale, setLocale: setLocaleWrapped, t }
  }, [locale, t])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useI18n() {
  return useContext(Ctx)
}
